import type { CodeQualityLevel, IPortfolioCodeQualityTrendBucket } from '@skystone/shared';
import type { TrendDirection } from '@/lib/dora-utils';
import { Card, CardContent, MetricInfoPopover } from '@/components/ui';
import { useCardWidth } from '@/hooks/use-card-width';
import { CARD_WIDTH_THRESHOLDS } from '@/lib/metric-view/responsive-thresholds';
import { cn } from '@/lib/utils';
import {
  describeSeries,
  EmptyChartMessage,
  isFullySeriesEmpty,
  shouldShowDots,
  Sparkline,
  StabilityAnnotation,
  type ISparkPoint,
} from '../metric-card-sparkline';
import { type IBucketAxisProps } from '@/lib/metric-view/bucket-format';
import { GradeAPercentChip } from './GradeAPercentChip';

const BORDER_LEFT: Record<CodeQualityLevel, string> = {
  high: 'border-l-4 border-l-success',
  medium: 'border-l-4 border-l-primary',
  low: 'border-l-4 border-l-warning',
  critical: 'border-l-4 border-l-error',
};

const TIER_LABEL: Record<CodeQualityLevel, string> = {
  high: 'Excellent',
  medium: 'Good',
  low: 'Watch',
  critical: 'Failing',
};

const TIER_ORDER: CodeQualityLevel[] = ['high', 'medium', 'low', 'critical'];

const TIER_PILL_BG: Record<CodeQualityLevel, string> = {
  high: 'bg-success/15 text-success',
  medium: 'bg-primary/15 text-primary',
  low: 'bg-warning/15 text-warning',
  critical: 'bg-error/15 text-error',
};

export interface ICodeQualityTrendCardProps {
  title: string;
  /** Italic small subtitle below the title. */
  definition: string;
  metricKey: string; // e.g. cq_coverage
  /** When true the chip reads "78% A"; when false it reads "78%" (coverage). */
  showGradeASuffix: boolean;
  buckets: IPortfolioCodeQualityTrendBucket[];
  aggregate: number | null;
  priorAggregate: number | null;
  level: CodeQualityLevel | null;
  /** Footer link target — typically the org-level SonarCloud dashboard. */
  sonarCloudUrl?: string;
  /** Forwarded to `Sparkline` — see `bucket-format.ts`. */
  axis?: IBucketAxisProps;
}

/** Direction + label for the inline delta beside the big number. Computed from
 *  current and prior aggregates. Coverage uses 0.5pp flat threshold; grade-A%
 *  uses 1pp per spec AC. */
function computeDelta(
  current: number | null,
  prior: number | null,
  flatThreshold: number,
): { direction: TrendDirection; label: string } | null {
  if (current === null || prior === null) return null;
  const diff = current - prior;
  const abs = Math.abs(diff);
  if (abs < flatThreshold) return { direction: 'flat', label: '0pp' };
  const sign = diff > 0 ? '+' : '-';
  const direction: TrendDirection = diff > 0 ? 'up' : 'down';
  return { direction, label: `${sign}${abs.toFixed(1)}pp` };
}

function bucketsToSeries(buckets: IPortfolioCodeQualityTrendBucket[]): ISparkPoint[] {
  return buckets.map((b) => ({ bucketStart: b.bucketStart, value: b.value }));
}

function formatHeadline(value: number | null, showGradeASuffix: boolean): string {
  if (value === null) return '—';
  const fixed = Number.isInteger(value) ? `${value}` : value.toFixed(1);
  return showGradeASuffix ? `${fixed}% A` : `${fixed}%`;
}

function CardHeader({
  title,
  definition,
  metricKey,
}: Readonly<{ title: string; definition: string; metricKey: string }>) {
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        <MetricInfoPopover metricKey={metricKey} />
      </div>
      <p className="text-xs italic text-muted-foreground">{definition}</p>
    </div>
  );
}

function ValueBlock({
  aggregate,
  level,
  showGradeASuffix,
  delta,
  hideBigNumber,
}: Readonly<{
  aggregate: number | null;
  level: CodeQualityLevel | null;
  showGradeASuffix: boolean;
  delta: { direction: TrendDirection; label: string } | null;
  hideBigNumber: boolean;
}>) {
  const headline = formatHeadline(aggregate, showGradeASuffix);
  let deltaNode: React.ReactNode = null;
  if (delta && !hideBigNumber) {
    const downSymbol = delta.direction === 'down' ? '▼' : '→';
    const symbol = delta.direction === 'up' ? '▲' : downSymbol;
    deltaNode = (
      <span className="ml-2 text-xs text-muted-foreground">
        {symbol} {delta.label}
      </span>
    );
  }

  return (
    <div className="mt-3 flex items-end justify-between">
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          Latest week
        </p>
        {!hideBigNumber && (
          <div className="flex items-baseline">
            <span className="text-2xl font-bold leading-none text-foreground">{headline}</span>
            {deltaNode}
          </div>
        )}
      </div>
      {!hideBigNumber && (
        <GradeAPercentChip
          percent={aggregate}
          level={level}
          showGradeASuffix={showGradeASuffix}
        />
      )}
    </div>
  );
}

function TierPositionStrip({ activeLevel }: Readonly<{ activeLevel: CodeQualityLevel | null }>) {
  return (
    <div
      className="mt-3 flex items-center gap-1.5"
      aria-label="Portfolio quality position"
      data-testid="tier-position-strip"
    >
      {TIER_ORDER.map((tier) => {
        const isActive = tier === activeLevel;
        return (
          <span
            key={tier}
            className={cn(
              'flex-1 rounded px-2 py-1 text-center text-[10px] font-semibold uppercase tracking-wider',
              isActive
                ? TIER_PILL_BG[tier]
                : 'bg-muted text-muted-foreground',
            )}
          >
            {TIER_LABEL[tier]}
          </span>
        );
      })}
    </div>
  );
}

function CardFooter({ sonarCloudUrl }: Readonly<{ sonarCloudUrl?: string }>) {
  if (!sonarCloudUrl) return null;
  return (
    <div className="mt-2 flex items-center justify-end border-t border-border/50 pt-2">
      <a
        href={sonarCloudUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="text-[10px] font-medium text-primary hover:underline"
      >
        View in SonarCloud →
      </a>
    </div>
  );
}

/**
 * One sparkline trend card for the Code Quality tab. Adapted from
 * `DoraMetricCard` shape — uses the shared sparkline helpers and replaces the
 * tier chip with `GradeAPercentChip`. Below `CARD_WIDTH_THRESHOLDS.trend.sparkline`
 * (400px) the sparkline + stability annotation are hidden and a `TierPositionStrip`
 * appears instead, matching the spec's responsive matrix.
 */
export function CodeQualityTrendCard({
  title,
  definition,
  metricKey,
  showGradeASuffix,
  buckets,
  aggregate,
  priorAggregate,
  level,
  sonarCloudUrl,
  axis,
}: Readonly<ICodeQualityTrendCardProps>) {
  const { ref, width } = useCardWidth<HTMLDivElement>();
  // width === 0 before first ResizeObserver tick; render the sparkline by
  // default so the initial paint is the canonical layout, not the fallback.
  const useListVariant = width > 0 && width < CARD_WIDTH_THRESHOLDS.trend.sparkline;

  const series = bucketsToSeries(buckets);
  const flatThreshold = showGradeASuffix ? 1 : 0.5;
  const delta = computeDelta(aggregate, priorAggregate, flatThreshold);
  const showEmptyState = isFullySeriesEmpty(aggregate, series);
  const shape = describeSeries(series);
  const showStabilityNote = !showEmptyState && shape.isStable;
  const showDots = showStabilityNote && shouldShowDots(series, shape);

  const borderClass = level ? BORDER_LEFT[level] : '';

  return (
    <Card ref={ref} className={cn('shadow-none', borderClass)}>
      <CardContent className="p-4 pt-4">
        <CardHeader title={title} definition={definition} metricKey={metricKey} />
        <ValueBlock
          aggregate={aggregate}
          level={level}
          showGradeASuffix={showGradeASuffix}
          delta={delta}
          hideBigNumber={showEmptyState}
        />
        {renderBody({
          useListVariant,
          showEmptyState,
          activeLevel: level,
          series,
          showDots,
          showStabilityNote,
          stabilityCount: shape.nonNullCount,
          showGradeASuffix,
          axis,
        })}
        <CardFooter sonarCloudUrl={sonarCloudUrl} />
      </CardContent>
    </Card>
  );
}

interface IBodyProps {
  useListVariant: boolean;
  showEmptyState: boolean;
  activeLevel: CodeQualityLevel | null;
  series: ISparkPoint[];
  showDots: boolean;
  showStabilityNote: boolean;
  stabilityCount: number;
  showGradeASuffix: boolean;
  axis?: IBucketAxisProps;
}

function renderBody(p: IBodyProps): React.ReactNode {
  if (p.showEmptyState) {
    return <EmptyChartMessage message="No history in this window." />;
  }
  if (p.useListVariant) {
    return <TierPositionStrip activeLevel={p.activeLevel} />;
  }
  return (
    <>
      <Sparkline
        series={p.series}
        formatValue={(n) => (Number.isInteger(n) ? `${n}` : n.toFixed(1))}
        unit={p.showGradeASuffix ? '% A' : '%'}
        showDots={p.showDots}
        axis={p.axis}
      />
      {p.showStabilityNote && <StabilityAnnotation count={p.stabilityCount} />}
    </>
  );
}
